import { useQuery } from '@tanstack/react-query';

import integrationService from '@/services/integration-service';
import type { AvailableIntegration } from '@/types/integrations';
import type { ApiResponse } from '@/types/user';
import { GET_AVAILABLE_INTEGRATIONS_QUERY } from '@/utils/query-keys';

/**
 * Fetches the list of public available integrations. The response is
 * cached in React Query and persisted into sessionStorage (see
 * `queryPersister.ts`) so subsequent navigations don't re-hit the
 * endpoint within the same tab session.
 */
export const useAvailableIntegrations = () => {
  return useQuery<
    ApiResponse<AvailableIntegration[]>,
    Error,
    AvailableIntegration[]
  >(
    [GET_AVAILABLE_INTEGRATIONS_QUERY],
    () => integrationService.getAvailableIntegrationsPublic(),
    {
      select: response =>
        response?.status && Array.isArray(response.data)
          ? [...response.data].sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            )
          : [],
      staleTime: 1000 * 60 * 30, // 30 min — catalog data changes rarely
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};
